import { createRouter, createWebHistory, type LocationQueryRaw, type RouteLocationNormalizedGeneric, type RouteRecordRaw } from "vue-router"
import { useUserStore } from "@/pinia/user-store"
import { useGeneralStore } from "@/pinia/general-store"

// These routes are not accessible to logged in users
export const guestExclusiveRoutes = [
  "login", "register", "reset-password", "forgot-password"
]

/**
 * Public routes are those that are accessible to the public.
 *
 * @var {string[]}
 */
export const publicRoutes = [
  ...guestExclusiveRoutes,
  // Workspaces are semi-public (the controller itself will check for authorization)
  "workspace",
  "terms-of-service"
]

const adminRoutes = [
  {
    path: "",
    name: "admin-dashboard",
    component: () => import("../views/admin/AdminDashboard.vue"),
    meta: { title: "Administration" },
  },
  {
    path: "users",
    name: "admin-users",
    component: () => import("../views/admin/UserList.vue"),
    meta: { title: "User Settings" }
  },
  {
    path: "users/create",
    name: "admin-user-create",
    component: () => import("../views/admin/UserCreate.vue"),
    meta: { title: "Create user" }
  },
  {
    path: "workspaces",
    name: "admin-workspaces",
    component: () => import("../views/admin/WorkspaceList.vue"),
    meta: { title: "Workspace List" }
  },
  {
    path: "workspaces/orphans",
    name: "admin-orphaned-workspaces",
    component: () => import("../views/admin/OrphanedWorkspacesList.vue"),
    meta: { title: "Orphaned Workspaces" }
  },
  {
    path: "roles",
    name: "admin-roles",
    component: () => import("../views/admin/RoleList.vue"),
    meta: { title: "View roles" }
  },
  {
    path: "roles/create",
    name: "admin-role-create",
    component: () => import("../views/admin/RoleCreate.vue"),
    meta: { title: "Create role" }
  },
  {
    path: "permissions",
    name: "admin-permissions",
    component: () => import("../views/admin/RolePermissions.vue"),
    meta: { title: "Set permissions" }
  },
  {
    path: "defaults/list",
    name: "admin-defaults-list",
    component: () => import("../views/admin/DefaultsFileList.vue"),
    meta: { title: "Defaults Files" }
  },
  {
    path: "export/queue",
    name: "admin-export-queue",
    component: () => import("../views/admin/ExportQueue.vue"),
    meta: { title: "Export Queue" }
  },
  {
    path: "defaults/edit/:uuid",
    name: "admin-defaults-edit",
    component: () => import("../views/admin/DefaultsFileEdit.vue"),
    meta: { title: "Edit defaults file" }
  },
  {
    path: "settings",
    name: "admin-server-settings",
    component: () => import("../views/admin/ServerSettings.vue"),
    meta: { title: "Server Settings" }
  },
  {
    path: "event-log",
    name: "admin-event-log",
    component: () => import("../views/admin/EventLog.vue"),
    meta: { title: "Event log" }
  },
] as const

const routes = [
  // GENERAL (SEMI-PRIVATE) ROUTES
  {
    // (.*) matches including path segments, this allows for navigating a file
    // system under the workspace UUID. This way, we basically have two
    // parameters: The first one describes the workspace (a.k.a. the top folder)
    // and wsPath (which may be empty!) that describes a subpath from there.
    path: "/workspace/:uuid/:wsPath(.*)",
    name: "workspace",
    component: () => import("../views/WorkspaceBrowser.vue"),
    meta: { title: "Workspace" }
  },
  // USER-SPECIFIC ROUTES
  {
    path: "/",
    name: "dashboard",
    component: () => import("../views/DashboardView.vue"),
    meta: { title: "Dashboard" }
  },
  {
    path: "/profile",
    name: "profile",
    component: () => import("../views/ProfileView.vue"),
    meta: { title: "Profile" }
  },
  {
    path: "/notifications",
    name: "notifications",
    component: () => import("../views/NotificationView.vue"),
    meta: { title: "notifications" }
  },
  // PUBLIC-ONLY ROUTES
  {
    path: "/login",
    name: "login",
    component: () => import("../views/LoginView.vue"),
    meta: { title: "Login" }
  },
  {
    path: "/signup",
    name: "register",
    component: () => import("../views/RegistrationView.vue"),
    meta: { title: "Sign up" }
  },
  {
    path: "/reset-password",
    name: "reset-password",
    component: () => import("../views/ResetPasswordView.vue"),
    meta: { title: "Reset password" }
  },
  {
    path: "/verify-email",
    name: "verify-email",
    component: () => import("../views/VerifyEmailView.vue"),
    meta: { title: "Verify Email" }
  },
  {
    path: "/forgot-password",
    name: "forgot-password",
    component: () => import("../views/RequestResetPasswordView.vue"),
    meta: { title: "Forgot password?" }
  },
  // ADMIN ROUTES
  {
    path: "/admin",
    name: "admin",
    // We nest them as standalone-children to make use of Vue Router's
    // "router-link-active" to better indicate an "area" the user is in
    // (especially relevant for mobile).
    children: adminRoutes
  },
  {
    path: "/edit-workspace/:uuid",
    name: "edit-workspace",
    component: () => import("../views/WorkspaceSettings.vue"),
    meta: { title: "Edit Workspace" }
  },
  {
    path: "/terms-of-service",
    name: "terms-of-service",
    component: () => import("../views/TOSView.vue"),
    meta: { title: "Terms of Service" }
  }
] as const

// Contains a list of all registered routes for better typing
export type RouteName = typeof routes[number]["name"] | typeof adminRoutes[number]["name"]

export const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: routes as unknown as RouteRecordRaw[]
})

/**
 * Uses the Vue router to navigate to the provided route. The routes are typed
 * so that you can only choose from the registered routes.
 *
 * @param  {RouteName}         routeName  The route to navigate to
 * @param  {LocationQueryRaw}  query      An optional query object
 */
export function navigateTo (routeName: RouteName, query?: LocationQueryRaw) {
  void router.push({ name: routeName, query })
}

/**
 * Returns whether the provided route is public or not. Can be used by the user
 * store to potentially perform the first redirect, if necessary.
 *
 * @param   {RouteLocationNormalizedGeneric}  route  The route
 *
 * @return  {boolean}                                Whether it's public.
 */
export function isRoutePublic (route: RouteLocationNormalizedGeneric): boolean {
  return publicRoutes.includes(route.name as string)
}

/**
 * Returns whether a given route is exclusively intended for non-logged-in users
 *
 * @param   {RouteLocationNormalizedGeneric}  route  The route
 *
 * @return  {boolean}                                Whether it's guest-only
 */
export function isRouteOnlyForGuests (route: RouteLocationNormalizedGeneric): boolean {
  return guestExclusiveRoutes.includes(route.name as string)
}

// Guard everything with a login
router.beforeEach(async (to) => {
  const userStore = useUserStore()
  if (userStore.isLoading) {
    return
  }

  // Always enforce login, but provide a redirect link so that the user can view
  // some document without having to navigate back there.
  if (userStore.user === null && !isRoutePublic(to)) {
    return { name: "login", query: { redirect: to.fullPath } }
  } else if (userStore.user !== null && isRouteOnlyForGuests(to)) {
    return { name: "dashboard" }
  }
})

router.afterEach((to) => {
  const generalStore = useGeneralStore()
  // Always update the document title
  document.title = to.meta.title !== undefined ? to.meta.title as string + ` | ${generalStore.appName}` : generalStore.appName
})
